import { Link, Navigate, useParams } from "react-router-dom"
import FacilitatorPanel from "@/components/FacilitatorPanel/FacilitatorPanel"
import RoundTransition from "@/components/RoundTransition/RoundTransition"
import RoundControls from "@/components/RoundControls/RoundControls"
import SessionHeader from "@/components/SessionHeader/SessionHeader"
import Cards from "./steps/Cards"
import Deep from "./steps/Deep"
import Job from "./steps/Job"
import Lobby from "./steps/Lobby"
import Pains from "./steps/Pains"
import PainsMonitor from "./steps/PainsMonitor"
import PickMonitor from "./steps/PickMonitor"
import Priorities from "./steps/Priorities"
import Debrief from "@/pages/Debrief/Debrief"
import Spinner from "@/components/Spinner/Spinner"
import StatusScreen from "@/components/StatusScreen/StatusScreen"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { invalidateFacilitatorIndex } from "@/hooks/useFacilitatorIndex"
import { useCustomerConnection, useSessionConnection } from "@/hooks/useSessionConnection"
import { useExpiryTick } from "@/hooks/useExpiryTick"
import { useSessionView } from "@/hooks/useSessionView"
import {
  ARCHIVE_RESTORE,
  SESSION_GONE_CUSTOMER_LINK,
  SESSION_GONE_DETAIL,
  SESSION_GONE_FOR_GUESTS,
  SESSION_GONE_TITLE,
  sessionHeldByCustomer,
} from "@/content/archive"
import { NAV_COPY, ROUND_COPY } from "@/content/rounds"
import { extendSession, restoreSession } from "@/services/sessions"
import { ROUTES } from "@/routes/routes"
import { customerDetailPath, joinPath } from "@/utils/routePaths"
import { useSessionStore } from "@/store"
import { selectAuthChecking, useAuthStore } from "@/store/authStore"
import {
  selectConnectionFailed,
  selectCustomerLoading,
  selectError,
  selectIsFacilitator,
  selectIsLoading,
  selectSession,
  selectSessionMissing,
  selectSurface,
} from "@/store/selectors"
import { isExpired } from "@/utils/sessionLifecycle"
import { removalState } from "@/utils/removal"
import styles from "@/styles/pages/Session.module.css"
import Container from "@/components/Container/Container"
import type { Phase, Round } from "@/types"

/** The writes a button on this screen waits on — see `useFirestoreWrite`. */
const EXTEND = "extend"
const RESTORE = "restore"

export default function Session() {
  const { sessionId } = useParams<{ sessionId: string }>()
  useSessionConnection(sessionId)

  const session = useSessionStore(selectSession)
  const loading = useSessionStore(selectIsLoading)
  const missing = useSessionStore(selectSessionMissing)
  const failed = useSessionStore(selectConnectionFailed)
  const error = useSessionStore(selectError)
  /*
   * Expiry is a moment passing, not a write, so nothing in the store changes
   * when it happens. A room left open would otherwise keep its controls looking
   * usable long past the deadline the rules now enforce, and every press would
   * come back refused.
   */
  const now = useExpiryTick([session?.expiresAt ?? null])
  const expired = isExpired(session, now)
  const surface = useSessionStore(selectSurface)
  const canDrive = useSessionStore(selectIsFacilitator)
  /*
   * Whether Firebase has answered "who is signed in" yet. It matters here, and
   * only because of the seat gate below: until it answers, the facilitator of
   * this session is indistinguishable from a stranger holding the link, and the
   * gate would bounce them to the join screen and back on every reload.
   */
  const authChecking = useAuthStore(selectAuthChecking)
  const { write, isPending } = useFirestoreWrite()
  /*
   * Where THIS browser is looking, which is the room's position unless it has
   * gone back to re-read a finished round. Held here and passed down: the header
   * navigates with it, the round screen is rendered from it, and the bottom bar
   * stands down while it is behind the room.
   */
  const view = useSessionView(session)

  /*
   * Only the facilitator subscribes to the customer, and only for the details
   * their own screens show. A guest may not read that document at all — it lists
   * every session run for the customer — so the room reads the name off
   * `session.customerName`, which is stamped onto the session for exactly this.
   */
  useCustomerConnection(canDrive ? session?.customerId : null)
  /* Read for one reason: a session cannot be restored out from under a removed
     customer — see the removal gate below. */
  const customer = useSessionStore((s) => s.customer)
  const customerLoading = useSessionStore(selectCustomerLoading)

  // The workspace is facilitator-only, so a guest is never offered it as a way
  // out of a dead end.
  const backToWork = canDrive ? <Link to={ROUTES.ROOT}>Back to customers</Link> : null

  // A route can only be reached with a param, but the type allows undefined.
  if (!sessionId) {
    return (
      <StatusScreen
        eyebrow="Session"
        title="No session in the link."
        detail="The address is missing a session id."
      >
        {backToWork}
      </StatusScreen>
    )
  }

  // Terminal first: a dead listener keeps delivering the last snapshot it had,
  // so a live-looking screen would quietly stop updating mid-round.
  if (failed) {
    return (
      <StatusScreen
        eyebrow="Connection lost"
        title="This screen stopped listening."
        detail="Firestore ended the connection and will not retry it. Reload to pick the session back up — nothing has been lost."
        raw={error}
      >
        <button type="button" onClick={() => window.location.reload()}>
          Reload
        </button>
      </StatusScreen>
    )
  }

  if (missing) {
    return (
      <StatusScreen
        eyebrow="Session"
        title="There is no session here."
        detail="The link may be mistyped, or the session was deleted. Ask the facilitator to send it again."
      >
        {backToWork}
      </StatusScreen>
    )
  }

  // `resolving` is a loading state too: this browser has a guest id but the
  // roster has not landed, so we cannot yet say whether it is still in the room.
  // So is a pending auth answer — see `authChecking`.
  if (loading || authChecking || surface === "resolving" || !session) {
    return <StatusScreen eyebrow="Session" title="Joining the room." waiting />
  }

  /*
   * Archived or deleted, and therefore over.
   *
   * Ahead of every other surface gate, including the seat gate below: a removed
   * session is not a room any more, so sending a guest to `/join` to take a seat
   * in it would be a redirect into a screen that refuses them. The rules agree —
   * `isLive` counts a removed session as dead, so every write beneath it comes
   * back denied from the moment the facilitator confirms.
   *
   * Two audiences, one state. The facilitator gets the shelf it is on and the
   * way back; the room is told the session ended, because which shelf is not
   * their business and "deleted" would read as though their hour went with it.
   */
  const gone = removalState(session)
  if (gone !== "active") {
    /* The CUSTOMER's removal outranks this session's, so restoring the session
       under a removed one hands it straight back to that removal: the room would
       revive, and the session would still be in no list. Null is "not resolved"
       — still in flight, or a customer this facilitator may not read — and is
       treated as present, the same call `utils/removal.sessionRemoval` makes. */
    const customerGone = removalState(customer)
    const heldBy =
      canDrive && gone === "archived" && customerGone !== "active" ? customerGone : null
    const customerName = customer?.name || session.customerName || ""

    return (
      <StatusScreen
        eyebrow="Session"
        title={canDrive ? SESSION_GONE_TITLE[gone] : "This session has ended."}
        detail={
          !canDrive
            ? SESSION_GONE_FOR_GUESTS
            : heldBy !== null
              ? `${SESSION_GONE_DETAIL} ${sessionHeldByCustomer(heldBy, customerName)}`
              : SESSION_GONE_DETAIL
        }
      >
        {/* Archived only: a deleted session is the one thing the app will not
            undo, and a button that put it back would make Delete and Archive the
            same act under two names. Nor while the customer is removed, and not
            before that is known — a button offered for one frame and withdrawn
            is worse than one that arrives a frame late. */}
        {canDrive && gone === "archived" && heldBy === null && !customerLoading ? (
          <button
            type="button"
            disabled={isPending(RESTORE)}
            aria-busy={isPending(RESTORE) || undefined}
            onClick={() =>
              write(async () => {
                await restoreSession(session.id)
                /* The listener above puts this screen back on its feet, but the
                   facilitator index is cached globally and nothing here reads
                   it: without this, a nav back to Sessions still omits the
                   restored session until its freshness window lapses. */
                invalidateFacilitatorIndex()
              }, RESTORE)
            }
          >
            {isPending(RESTORE) ? <Spinner size="sm" /> : null}
            {ARCHIVE_RESTORE}
          </button>
        ) : null}
        {/* Where the restore actually lives, on the record that is holding this
            one. Archived only, like the customer's own Restore. */}
        {heldBy === "archived" ? (
          <Link to={customerDetailPath(session.customerId)}>{SESSION_GONE_CUSTOMER_LINK}</Link>
        ) : null}
        {backToWork}
      </StatusScreen>
    )
  }

  if (surface === "removed") {
    return (
      <StatusScreen
        eyebrow="Session"
        title="You are not in this session any more."
        detail="Your place was removed. Open the link again and you will be back on the roster."
      >
        <Link to={joinPath(session.id)}>Join again</Link>
      </StatusScreen>
    )
  }

  /*
   * Nobody sits in this room anonymously.
   *
   * The session document is world-readable — its id IS the share link — so
   * without this, anyone who opened the link watched the round the room was on
   * while the app had no idea who they were: nothing they typed could be
   * attributed to them, they were absent from the roster, and the facilitator's
   * "everyone is ready" gate was counting a table that did not include them.
   *
   * The join screen is where a name is taken, so that is where they go, and it
   * is also what tells the other two stories the link can have — finished, and
   * expired — hence ahead of the expiry check below. Once this browser holds a
   * seat it walks straight back here.
   */
  if (surface === "spectator") {
    return <Navigate to={joinPath(session.id)} replace />
  }

  /*
   * The two ways a session stops. The facilitator still gets the room — they are
   * the one who reopens or reads it back — but nobody writes into an expired
   * session, so telling the room plainly beats a screen whose buttons silently
   * fail against the rules.
   */
  if (expired && !canDrive) {
    return (
      <StatusScreen
        eyebrow="Session"
        title="This session has expired."
        detail="Sessions stay open for a day. Ask the facilitator to reopen it if the room is still working."
      />
    )
  }

  /*
   * A guest sitting in the lobby gets the artifact's waiting room: one centred
   * column on the bare felt, no bar above it and none below. Nothing on that
   * screen is theirs to drive — the rail is the facilitator's, and the bottom
   * bar would say in small type what the middle of the table already says — so
   * the chrome comes back with the first round. The curtain still mounts: it is
   * how the room is told the session has started.
   */
  if (session.round === "lobby" && surface === "participant") {
    return (
      <Container bare>
        <Lobby />
        <RoundTransition canDrive={canDrive} />
      </Container>
    )
  }

  /* The one round whose rail carries a second panel, and the one whose heading
   * is a plain title and objective — which is what `railAtResult` measures.
   *
   * Read off the VIEW, not the session: both are facts about the round on
   * screen. `railAtResult` clears that round's heading, and a roster of who is
   * picking is only legible beside the deck it counts. */
  const picking = view.round === "cards" && view.phase === "pick"
  const writingPains = view.round === "pains" && view.phase === "write"

  return (
    <Container bare>
      <SessionHeader view={view} />

      {/*
       * An expired session is read-only for the room and every write is refused
       * by the rules, so the facilitator gets the one control that fixes it
       * rather than a screen whose buttons quietly fail. Which is also why the
       * round below is rendered read-only and the rail, the bottom bar and any
       * curtain stand down: leaving them up offers a facilitator a session they
       * can still run, and every press comes back as an error toast.
       */}
      {expired ? (
        <div className={styles.expired}>
          <p className={styles.expiredBody}>
            This session's link has expired. Nobody can join or write until it is reopened.
          </p>
          <button
            type="button"
            className={styles.expiredAction}
            disabled={isPending(EXTEND)}
            aria-busy={isPending(EXTEND) || undefined}
            onClick={() => write(() => extendSession(session.id), EXTEND)}
          >
            {isPending(EXTEND) ? <Spinner size="sm" /> : null}
            Reopen for another day
          </button>
        </div>
      ) : null}

      {/*
       * The round, and beside it the facilitator's rail. Every screen reads the
       * same session document; `canDrive` is what decides who may write.
       *
       * The round on screen is the VIEW's, not the session's — they differ only
       * while this browser is reading a finished round back, and `readOnly` is
       * what makes that safe: a screen behind the room must not write, or it
       * files answers against a beat nothing is counting any more.
       */}
      <div className={styles.stage}>
        <main className={styles.round}>
          {view.reviewing ? (
            <p className={styles.reviewing}>
              <span className={styles.reviewingTitle}>
                {NAV_COPY.reviewing(ROUND_COPY[view.round].label)}
              </span>
              <span className={styles.reviewingNote}>
                {NAV_COPY.readOnly} · {NAV_COPY.roomIsOn(ROUND_COPY[session.round].label)}
              </span>
              <button type="button" className={styles.reviewingBack} onClick={view.live}>
                {NAV_COPY.backToLive}
              </button>
            </p>
          ) : null}

          <RoundScreen
            round={view.round}
            phase={view.phase}
            canDrive={canDrive}
            readOnly={view.reviewing || expired}
          />
        </main>
        {/* The panel follows the ROOM, not the view: it is the facilitator's
         * script for the beat they are actually running, and a glance backwards
         * does not change what they are about to say. Not rendered at all for a
         * guest, or for the lobby — which has its own rules panel in this space
         * — because an empty rail still reserves its column, and a round with no
         * Controls panel beside it should use the whole width, not leave a gap
         * where one would have been.
         *
         * The Job round renders its own copy instead, inside its statement
         * columns — see `Job.tsx` — so that panel can stretch to the fields'
         * and result box's height. Skipped here or the room would get two.
         *
         * The Cards round's pick phase hangs a second panel below it: who has
         * picked what belongs beside the deck the room is picking from, not
         * inside the paper the cards are dealt on — see `PickMonitor`. It is
         * mounted here because the rail is this screen's, and `CardsPick`
         * cannot render into a column it does not own. The Pains write phase
         * hangs its roster there the same way — see `PainsMonitor`.
         *
         * Whether the rail is here at all is the VIEW's question, not the
         * room's: it is skipped for the two rounds that draw their own panel
         * into their own layout, and those are rendered from `view.round`. Read
         * off the session instead, a facilitator reading the job round back
         * while the room was elsewhere got two panels, and one reading anything
         * else back while the room was on job got none.
         *
         * The monitors stand down during a review: their counts are the beat
         * the room is on, and beside a board from an earlier one they would be
         * read as that board's. */}
        {canDrive && !expired && view.round !== "lobby" && view.round !== "job" ? (
          <div className={picking ? `${styles.rail} ${styles.railAtResult}` : styles.rail}>
            <FacilitatorPanel canDrive />
            {picking && !view.reviewing ? <PickMonitor /> : null}
            {writingPains && !view.reviewing ? <PainsMonitor /> : null}
          </div>
        ) : null}
      </div>

      {expired ? null : <RoundControls canDrive={canDrive} view={view} />}

      {/* Renders only when the session has an overlay. */}
      {expired ? null : <RoundTransition canDrive={canDrive} />}
    </Container>
  )
}

/*
 * One screen per round.
 *
 * A switch rather than a lookup table so the exhaustiveness check is real: adding
 * a round to `ROUND_ORDER` without a screen for it becomes a type error here
 * rather than a blank page at the workshop.
 *
 * `phase` is passed in rather than read off the store by each screen, because
 * the phase on screen is not always the session's — see `useSessionView`.
 */
function RoundScreen({
  round,
  phase,
  canDrive,
  readOnly,
}: {
  round: Round
  phase: Phase
  canDrive: boolean
  readOnly: boolean
}) {
  switch (round) {
    case "lobby":
      return <Lobby />
    case "pains":
      return <Pains phase={phase} canDrive={canDrive} readOnly={readOnly} />
    case "job":
      return <Job phase={phase} canDrive={canDrive} readOnly={readOnly} />
    case "cards":
      return <Cards phase={phase} canDrive={canDrive} readOnly={readOnly} />
    case "priorities":
      return <Priorities canDrive={canDrive} readOnly={readOnly} />
    case "deep":
      return <Deep canDrive={canDrive} readOnly={readOnly} />
    case "wrap":
      return <Debrief />
  }
}
